/**
 * useVoiceInput - Prompt 14
 * Hook for voice input using Web Speech Recognition API
 * Provides real-time transcription with interim results
 */
import { useState, useEffect, useRef, useCallback } from 'react'

interface UseVoiceInputOptions {
  /** Auto-stop after silence (ms). Default: 5000 */
  silenceTimeout?: number
  /** Language code. Default: 'en-US' */
  language?: string
  /** Continuous listening mode. Default: false */
  continuous?: boolean
}

interface UseVoiceInputReturn {
  isListening: boolean
  isSupported: boolean
  transcript: string
  interimTranscript: string
  startListening: () => void
  stopListening: () => void
  toggleListening: () => void
  clearTranscript: () => void
  error: string | null
  clearError: () => void
}

// SpeechRecognition types for browser API
interface SpeechRecognitionResultItem {
  transcript: string
  confidence: number
}

interface SpeechRecognitionResultLocal {
  isFinal: boolean
  length: number
  item(index: number): SpeechRecognitionResultItem
  [index: number]: SpeechRecognitionResultItem
}

interface SpeechRecognitionResultList {
  length: number
  item(index: number): SpeechRecognitionResultLocal
  [index: number]: SpeechRecognitionResultLocal
}

interface SpeechRecognitionEventLocal extends Event {
  results: SpeechRecognitionResultList
  resultIndex: number
}

interface SpeechRecognitionErrorEventLocal extends Event {
  error: string
  message?: string
}

interface SpeechRecognitionInstance extends EventTarget {
  continuous: boolean
  interimResults: boolean
  lang: string
  maxAlternatives: number
  onresult: ((event: SpeechRecognitionEventLocal) => void) | null
  onend: (() => void) | null
  onerror: ((event: SpeechRecognitionErrorEventLocal) => void) | null
  onspeechstart: (() => void) | null
  start(): void
  stop(): void
  abort(): void
}

interface SpeechRecognitionConstructor {
  new (): SpeechRecognitionInstance
}

declare global {
  interface Window {
    SpeechRecognition?: SpeechRecognitionConstructor
    webkitSpeechRecognition?: SpeechRecognitionConstructor
  }
}

export function useVoiceInput(options: UseVoiceInputOptions = {}): UseVoiceInputReturn {
  const { silenceTimeout = 5000, language = 'en-US', continuous = false } = options

  const [isListening, setIsListening] = useState(false)
  const [transcript, setTranscript] = useState('')
  const [interimTranscript, setInterimTranscript] = useState('')
  const [error, setError] = useState<string | null>(null)
  const recognitionRef = useRef<SpeechRecognitionInstance | null>(null)
  const silenceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const isSupported = typeof window !== 'undefined' &&
    ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window)

  useEffect(() => {
    if (!isSupported) return
    const SpeechRecognitionClass = window.SpeechRecognition || window.webkitSpeechRecognition
    if (!SpeechRecognitionClass) return
    const recognition = new SpeechRecognitionClass()
    recognition.continuous = continuous
    recognition.interimResults = true
    recognition.lang = language
    recognition.maxAlternatives = 1

    recognition.onresult = (event: SpeechRecognitionEventLocal) => {
      let interim = ''
      let final = ''
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i]
        if (result.isFinal) { final += result[0].transcript }
        else { interim += result[0].transcript }
      }
      if (final) {
        setTranscript(prev => (prev + ' ' + final).trim())
        setInterimTranscript('')
        if (silenceTimerRef.current) { clearTimeout(silenceTimerRef.current); silenceTimerRef.current = null }
      } else {
        setInterimTranscript(interim)
        if (silenceTimerRef.current) { clearTimeout(silenceTimerRef.current) }
        silenceTimerRef.current = setTimeout(() => { recognition.stop() }, silenceTimeout)
      }
    }

    recognition.onend = () => {
      setIsListening(false)
      setInterimTranscript('')
      if (silenceTimerRef.current) { clearTimeout(silenceTimerRef.current); silenceTimerRef.current = null }
    }

    recognition.onerror = (event: SpeechRecognitionErrorEventLocal) => {
      setIsListening(false)
      setInterimTranscript('')
      switch (event.error) {
        case 'not-allowed': setError('Microphone access denied. Please enable it in browser settings.'); break
        case 'no-speech': setError('No speech detected'); setTimeout(() => setError(null), 2000); break
        case 'audio-capture': setError('No microphone found. Please connect a microphone.'); break
        case 'network': setError('Network error. Please check your connection.'); break
        case 'aborted': break
        default: setError(`Voice input error: ${event.error}`)
      }
    }

    recognition.onspeechstart = () => {
      if (silenceTimerRef.current) { clearTimeout(silenceTimerRef.current); silenceTimerRef.current = null }
    }

    recognitionRef.current = recognition
    return () => { recognition.abort(); if (silenceTimerRef.current) { clearTimeout(silenceTimerRef.current) } }
  }, [isSupported, language, continuous, silenceTimeout])

  const startListening = useCallback(() => {
    if (!recognitionRef.current || isListening) return
    setError(null); setTranscript(''); setInterimTranscript('')
    try {
      recognitionRef.current.start()
      setIsListening(true)
      silenceTimerRef.current = setTimeout(() => { if (recognitionRef.current) { recognitionRef.current.stop() } }, silenceTimeout)
    } catch (e) { console.warn('Voice input start error:', e) }
  }, [isListening, silenceTimeout])

  const stopListening = useCallback(() => {
    if (!recognitionRef.current || !isListening) return
    recognitionRef.current.stop()
    setIsListening(false)
    if (silenceTimerRef.current) { clearTimeout(silenceTimerRef.current); silenceTimerRef.current = null }
  }, [isListening])

  const toggleListening = useCallback(() => {
    if (isListening) { stopListening() } else { startListening() }
  }, [isListening, startListening, stopListening])

  const clearTranscript = useCallback(() => { setTranscript(''); setInterimTranscript('') }, [])
  const clearError = useCallback(() => { setError(null) }, [])

  return { isListening, isSupported, transcript, interimTranscript, startListening, stopListening, toggleListening, clearTranscript, error, clearError }
}

export default useVoiceInput
